import type { DateRange } from '@domain/value-objects/DateRange';

/**
 * En qué orden se listan las personas en la vista «Por persona».
 *
 * El tablero venía en el orden en que el repositorio devolvía la gente, que es el
 * del padrón: para saber quién sale primero había que recorrer treinta y siete
 * filas siguiendo las barras con la vista. Ordenarlo por la ausencia más próxima
 * pone arriba lo que se está preguntando y, de paso, hace que las barras bajen en
 * cascada de izquierda a derecha — el tablero pasa a leerse como un calendario en
 * vez de como una lista con marcas.
 *
 * **Cuenta lo que está en revisión.** Una solicitud sin firmar sigue siendo un
 * hueco que alguien tiene que considerar al elegir fechas, y esa es toda la razón
 * de esta pantalla; el tablero ya la dibuja, sólo que atenuada. Aquí no se
 * distingue: lo que se ordena es *cuándo*, no *qué tan seguro*.
 */

/** Lo poco que el orden necesita saber de una persona. */
export interface OrderablePerson {
  readonly id: string;
  readonly displayName: string;
}

/** Lo poco que necesita de una ausencia. */
export interface OrderableAbsence {
  readonly range: DateRange;
}

/**
 * El día en que empieza la ausencia más temprana de alguien, o `null` si no tiene
 * ninguna en la ventana.
 *
 * Se mide por la fecha de inicio **sin recortar a la ventana**: quien ya estaba
 * fuera antes de que el mes empezara sale antes que quien empieza el día 1, que es
 * lo que su barra ya dice al arrancar pegada al borde izquierdo.
 */
const earliestStart = (absences: readonly OrderableAbsence[]): number | null => {
  let earliest: number | null = null;
  for (const absence of absences) {
    const day = absence.range.start.epochDay;
    if (earliest === null || day < earliest) earliest = day;
  }
  return earliest;
};

/**
 * Ordena por ausencia más próxima, y a quien no tiene ninguna lo manda al final.
 *
 * Sin ausencia no hay «próxima», así que el desempate no es un cero —que los
 * pondría primero, delante de justo lo que se viene a ver— sino el final de la
 * lista, por nombre. Ahí siguen estando y siguen siendo alcanzables; lo que no
 * hacen es ocupar las filas que se leen primero.
 *
 * Entre dos que empiezan el mismo día decide el nombre, para que el orden no
 * cambie entre dos cargas que traen la misma información.
 */
export const byNextAbsence = <P extends OrderablePerson, A extends OrderableAbsence>(
  people: readonly P[],
  absencesByPerson: ReadonlyMap<string, readonly A[]>,
): readonly P[] =>
  [...people].sort((a, b) => {
    const startA = earliestStart(absencesByPerson.get(a.id) ?? []);
    const startB = earliestStart(absencesByPerson.get(b.id) ?? []);

    if (startA === null && startB === null) return a.displayName.localeCompare(b.displayName, 'es');
    if (startA === null) return 1;
    if (startB === null) return -1;
    if (startA !== startB) return startA - startB;

    return a.displayName.localeCompare(b.displayName, 'es');
  });
